import { useState } from "react";
import { ArrowLeft, RotateCcw } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const STEPS = [
  {
    node: "Outcome",
    title: "The failure becomes visible.",
    desc: "A transaction fails at the point of service. A case stalls. An error surfaces to a citizen, a customer, or an operator. This is where most investigations begin — and where many of them end.",
  },
  {
    node: "Decision",
    title: "The decision that produced it.",
    desc: "An automated rule routed the case using a threshold set months earlier — reasonable at the time, wrong under current conditions.",
  },
  {
    node: "System",
    title: "The system behavior behind it.",
    desc: "The application returned an error state without context. The software did exactly what it was configured to do.",
  },
  {
    node: "Integration",
    title: "The integration upstream.",
    desc: "An interface between two systems began returning stale records after a version change. No alert fired.",
  },
  {
    node: "Data",
    title: "The data feeding the integration.",
    desc: "A source table had not refreshed in thirty-six hours. The values were valid — they were simply no longer true.",
  },
  {
    node: "Process",
    title: "The process around the data.",
    desc: "No reconciliation step existed to detect the growing gap between source systems and everything downstream of them.",
  },
  {
    node: "Origin",
    title: "Where the outcome became inevitable.",
    desc: "A policy change altered verification timing. It was never propagated to the integration layer. Every downstream failure followed necessarily from this point — and no amount of fixing at the surface would have resolved it.",
  },
];

export default function FollowTheErrorSection() {
  const [idx, setIdx] = useState(0);
  const atOrigin = idx === STEPS.length - 1;

  return (
    <section data-testid="follow-error-section" className="bg-navy text-white">
      <div className="mx-auto max-w-7xl px-6 py-20 sm:py-28">
        <div className="grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="mb-6 flex items-center gap-3 font-mono text-[11px] tracking-[0.3em] text-slate-400">
                <span className="h-px w-10 bg-slate-600" aria-hidden="true" />
                05 — FOLLOW THE ERROR BACKWARD
              </p>
            </Reveal>
            <Reveal delay={0.08}>
              <h2 className="font-serif text-3xl leading-tight tracking-tight sm:text-4xl lg:text-5xl">
                Find where the outcome became inevitable.
              </h2>
            </Reveal>
            <Reveal delay={0.16}>
              <p className="mt-8 max-w-md leading-relaxed text-slate-300">
                The point where an error becomes visible is not necessarily the point where
                it originated. Improve Systems traces outcomes backward through decisions,
                systems, integrations, data, processes, and people — to the conditions where
                the outcome became inevitable.
              </p>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              <div className="flex items-center" role="group" aria-label="Backward trace steps">
                {STEPS.map((s, i) => (
                  <div key={s.node} className="flex flex-1 items-center last:flex-none">
                    <button
                      type="button"
                      data-testid={`trace-step-button-${i}`}
                      onClick={() => setIdx(i)}
                      aria-label={`Trace step: ${s.node}`}
                      aria-current={i === idx ? "step" : undefined}
                      className={`relative flex h-9 w-9 items-center justify-center rounded-full border font-mono text-[9px] transition-colors duration-300 ${
                        i === idx
                          ? "border-accent bg-accent text-white"
                          : i < idx
                            ? "border-slate-500 text-slate-300"
                            : "border-white/20 text-slate-500"
                      }`}
                    >
                      {String(i + 1).padStart(2, "0")}
                      {atOrigin && i === idx && (
                        <span className="animate-pulse-node absolute inset-0 rounded-full border border-accent" aria-hidden="true" />
                      )}
                    </button>
                    {i < STEPS.length - 1 && (
                      <span
                        className={`h-px flex-1 transition-colors duration-500 ${
                          i < idx ? "bg-accent" : "bg-white/15"
                        }`}
                        aria-hidden="true"
                      />
                    )}
                  </div>
                ))}
              </div>

              <div className="mt-10 border border-white/15 bg-white/5 p-8 sm:p-10">
                <p className="font-mono text-[10px] tracking-[0.3em] text-slate-400" data-testid="trace-step-label">
                  TRACE-BACK — STEP {String(idx + 1).padStart(2, "0")} / 07 ·{" "}
                  {STEPS[idx].node.toUpperCase()}
                </p>
                <h3 className="mt-5 font-serif text-2xl leading-snug sm:text-3xl" data-testid="trace-step-title">
                  {STEPS[idx].title}
                </h3>
                <p className="mt-4 max-w-xl leading-relaxed text-slate-300" data-testid="trace-step-desc">
                  {STEPS[idx].desc}
                </p>
                <div className="mt-8 flex flex-wrap items-center gap-4">
                  <button
                    type="button"
                    data-testid="trace-next-button"
                    onClick={() => setIdx((v) => Math.min(v + 1, STEPS.length - 1))}
                    disabled={atOrigin}
                    className="inline-flex items-center gap-2 bg-white px-6 py-3.5 text-sm tracking-wide text-navy transition-colors duration-300 hover:bg-slate-200 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    {atOrigin ? "Origin reached" : "Trace one step back"}
                  </button>
                  <button
                    type="button"
                    data-testid="trace-reset-button"
                    onClick={() => setIdx(0)}
                    className="inline-flex items-center gap-2 border border-white/25 px-6 py-3.5 text-sm tracking-wide text-white transition-colors duration-300 hover:border-white"
                  >
                    <RotateCcw className="h-3.5 w-3.5" />
                    Reset
                  </button>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
